import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface TablePaginationProps {
  currentPage: number;
  totalItems: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rows: number) => void;
}

const PAGE_SIZE_OPTIONS = [
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "Todos", value: Infinity },
];

export function TablePagination({ currentPage, totalItems, rowsPerPage, onPageChange, onRowsPerPageChange }: TablePaginationProps) {
  const totalPages = rowsPerPage === Infinity ? 1 : Math.ceil(totalItems / rowsPerPage);
  const start = rowsPerPage === Infinity ? 1 : (currentPage - 1) * rowsPerPage + 1;
  const end = rowsPerPage === Infinity ? totalItems : Math.min(currentPage * rowsPerPage, totalItems);

  if (totalItems === 0) return null;

  return (
    <div className="flex items-center justify-between px-4 py-2 border-t border-border">
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-muted-foreground">Filas:</span>
        <Select
          value={rowsPerPage === Infinity ? "all" : String(rowsPerPage)}
          onValueChange={(v) => onRowsPerPageChange(v === "all" ? Infinity : parseInt(v))}
        >
          <SelectTrigger className="h-7 w-[70px] text-xs border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((opt) => (
              <SelectItem key={opt.label} value={opt.value === Infinity ? "all" : String(opt.value)} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {start}–{end} de {totalItems}
        </span>
        {totalPages > 1 && (
          <>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function usePagination(defaultPerPage = 20) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultPerPage);

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const resetPage = () => setCurrentPage(1);

  const paginate = <T,>(items: T[]): T[] => {
    if (rowsPerPage === Infinity) return items;
    const start = (currentPage - 1) * rowsPerPage;
    return items.slice(start, start + rowsPerPage);
  };

  return { currentPage, rowsPerPage, setCurrentPage, handleRowsPerPageChange, resetPage, paginate };
}
